/**
 * URL-backed query state + data loading for Team Admin → Identity Providers.
 *
 * The federation list is small (a school has one or two providers), so the API
 * returns it whole and the search / protocol filter / status filter / sort /
 * pagination are applied client-side — but they still live in the address bar,
 * so a refresh, a bookmark and a shared link restore the same view. Same idiom
 * as `use-stat-fields.ts`.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useApiClient } from '../../app/providers';
import { useSession } from '../../app/session-context';
import type { ApiClient } from '../../lib/api-client';
import { ApiError } from '../../lib/api-error';
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  TIER_LIMIT_ERROR_CODE,
  TIER_LIMIT_STATUS,
} from '../../lib/constants';
import { PERSONAS, type Persona } from '../../lib/personas';
import {
  enumParam,
  intChoiceParam,
  intParam,
  readUrlState,
  textParam,
  writeUrlState,
  URL_PARAM_KEYS,
} from '../../lib/url-state';

/** `/auth/v1/identity-providers` — matches `services/auth/src/routes/identity-providers.ts`. */
export const IDENTITY_PROVIDERS_PATH = '/auth/v1/identity-providers';

export const FEDERATION_PROTOCOLS = ['saml2', 'scim2'] as const;
export type FederationProtocol = (typeof FEDERATION_PROTOCOLS)[number];

/** The plan feature that unlocks SAML / SCIM, and the tier that carries it. */
export const FEDERATION_FEATURE = 'saml_scim';
export const FEDERATION_MINIMUM_PLAN = 'elite';

/**
 * When the team's own plan already says SSO is not included, the page knows
 * it is locked without firing a request that can only answer 402 — it builds
 * the same tier-limit error the auth service would send. `null` = unknown (the
 * billing read failed or the plan includes SSO): ask the auth service.
 */
export async function knownFederationLock(
  apiClient: ApiClient,
  tenantId: string | null,
  signal: AbortSignal
): Promise<ApiError | null> {
  if (tenantId === null) return null;
  try {
    const view = await apiClient.getBillingSubscription(tenantId, signal);
    const features = view.entitlements.features as unknown as Record<string, unknown>;
    if (features[FEDERATION_FEATURE] !== false) return null;
    return new ApiError({
      status: TIER_LIMIT_STATUS,
      code: TIER_LIMIT_ERROR_CODE,
      message:
        'SAML 2.0 and SCIM 2.0 federation are available on the Elite plan. Upgrade to connect your school or district identity provider.',
      details: {
        limit_key: FEDERATION_FEATURE,
        current_plan: view.entitlements.plan_name,
        minimum_plan: FEDERATION_MINIMUM_PLAN,
      },
    });
  } catch {
    return null;
  }
}

export const PROTOCOL_LABELS: Record<FederationProtocol, string> = {
  saml2: 'SAML 2.0',
  scim2: 'SCIM 2.0',
};

/**
 * Personas a team admin may tick. `platform_admin` is Gridiron staff and can
 * never be federated — the auth service rejects it with 400 as well.
 */
export const CONFIGURABLE_PERSONAS: readonly Persona[] = PERSONAS.filter(
  (persona) => persona !== 'platform_admin'
);

export interface IdentityProviderView {
  provider_id: string;
  protocol: FederationProtocol;
  slug: string;
  display_name: string;
  is_enabled: boolean;
  default_persona: Persona | null;
  allowed_personas: Persona[];
  saml_idp_entity_id: string | null;
  saml_sso_url: string | null;
  has_certificate: boolean;
  has_scim_token: boolean;
  scim_user_mapping: Record<string, string>;
  keycloak_realm_name: string | null;
  realm_provisioned_at: string | null;
  last_metadata_sync_at: string | null;
  last_used_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface FederationEndpoints {
  saml_acs_url: string;
  saml_entity_id: string;
  saml_metadata_url: string;
  scim_base_url: string;
  is_realm_provisioning_available: boolean;
}

export type ProviderSortField = 'display_name' | 'protocol' | 'status' | 'last_used_at';

export interface ProvidersQueryState {
  search: string;
  protocol: FederationProtocol | 'all';
  status: 'all' | 'enabled' | 'disabled';
  sortBy: ProviderSortField;
  sortOrder: 'asc' | 'desc';
  page: number;
  pageSize: number;
}

const SORT_FIELDS: readonly ProviderSortField[] = [
  'display_name',
  'protocol',
  'status',
  'last_used_at',
];

/** Declarative screen state (shared url-state kit): parse is total, defaults stay off the URL. */
const PROVIDERS_URL_STATE = {
  search: textParam(URL_PARAM_KEYS.search),
  protocol: enumParam('protocol', [...FEDERATION_PROTOCOLS, 'all'] as const, 'all'),
  status: enumParam('status', ['all', 'enabled', 'disabled'] as const, 'all'),
  sortBy: enumParam(URL_PARAM_KEYS.sortBy, SORT_FIELDS, 'display_name'),
  sortOrder: enumParam(URL_PARAM_KEYS.sortOrder, ['asc', 'desc'] as const, 'asc'),
  page: intParam(URL_PARAM_KEYS.page, { fallback: 1, min: 1 }),
  pageSize: intChoiceParam(URL_PARAM_KEYS.pageSize, PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE),
};

function readQuery(params: URLSearchParams): ProvidersQueryState {
  return readUrlState(PROVIDERS_URL_STATE, params);
}

function compare(a: IdentityProviderView, b: IdentityProviderView, by: ProviderSortField): number {
  switch (by) {
    case 'protocol':
      return a.protocol.localeCompare(b.protocol);
    case 'status':
      return Number(b.is_enabled) - Number(a.is_enabled);
    case 'last_used_at':
      return (a.last_used_at ?? '').localeCompare(b.last_used_at ?? '');
    default:
      return a.display_name.localeCompare(b.display_name);
  }
}

export interface ProvidersController {
  query: ProvidersQueryState;
  setQuery: (patch: Partial<ProvidersQueryState>) => void;
  /** The current page after search / filter / sort. */
  providers: readonly IdentityProviderView[];
  /** Every provider the tenant has, regardless of the current filters. */
  allProviders: readonly IdentityProviderView[];
  total: number;
  endpoints: FederationEndpoints | null;
  isLoading: boolean;
  error: string | null;
  /** Set when the API answered 402 — the page shows its locked preview + upgrade CTA. */
  tierError: ApiError | null;
  reload: () => void;
}

interface ProvidersEnvelope {
  data: IdentityProviderView[];
  meta: FederationEndpoints;
}

export function useIdentityProviders(): ProvidersController {
  const apiClient = useApiClient();
  const tenantId = useSession().session?.tenantId ?? null;
  const [params, setParams] = useSearchParams();
  const query = useMemo(() => readQuery(params), [params]);

  const [all, setAll] = useState<readonly IdentityProviderView[]>([]);
  const [endpoints, setEndpoints] = useState<FederationEndpoints | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tierError, setTierError] = useState<ApiError | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const setQuery = useCallback(
    (patch: Partial<ProvidersQueryState>) => {
      const next = { ...query, ...patch };
      // Any filter change resets to page 1 unless the caller paged explicitly.
      if (patch.page === undefined) next.page = 1;
      setParams(writeUrlState(PROVIDERS_URL_STATE, next, params), { replace: true });
    },
    [query, params, setParams]
  );

  useEffect(() => {
    const controller = new AbortController();
    let isCurrent = true;
    setIsLoading(true);
    knownFederationLock(apiClient, tenantId, controller.signal)
      .then((lock) => {
        if (lock !== null) throw lock;
        return apiClient.request<ProvidersEnvelope>(IDENTITY_PROVIDERS_PATH, {
          signal: controller.signal,
          // The page renders its own locked preview for a 402, so the app-wide
          // tier-limit modal must not open over it (and leave nothing behind
          // once dismissed).
          handlesTierLimit: true,
        });
      })
      .then((envelope) => {
        if (!isCurrent) return;
        setAll(envelope.data ?? []);
        setEndpoints(envelope.meta ?? null);
        setError(null);
        setTierError(null);
      })
      .catch((caught: unknown) => {
        if (!isCurrent || controller.signal.aborted) return;
        if (ApiError.isApiError(caught) && caught.status === 402) {
          setTierError(caught);
          setError(null);
          return;
        }
        setError(
          ApiError.isApiError(caught)
            ? caught.message
            : 'Identity providers could not be loaded. Please try again.'
        );
      })
      .finally(() => {
        if (isCurrent) setIsLoading(false);
      });
    return () => {
      isCurrent = false;
      controller.abort();
    };
  }, [apiClient, reloadToken, tenantId]);

  const filtered = useMemo(() => {
    const needle = query.search.trim().toLowerCase();
    const matched = all.filter((provider) => {
      if (query.protocol !== 'all' && provider.protocol !== query.protocol) return false;
      if (query.status === 'enabled' && !provider.is_enabled) return false;
      if (query.status === 'disabled' && provider.is_enabled) return false;
      if (needle === '') return true;
      return [provider.display_name, provider.slug, provider.saml_idp_entity_id ?? '']
        .join(' ')
        .toLowerCase()
        .includes(needle);
    });
    const direction = query.sortOrder === 'desc' ? -1 : 1;
    return [...matched].sort((a, b) => compare(a, b, query.sortBy) * direction);
  }, [all, query.protocol, query.search, query.sortBy, query.sortOrder, query.status]);

  const start = (query.page - 1) * query.pageSize;

  return {
    query,
    setQuery,
    providers: filtered.slice(start, start + query.pageSize),
    allProviders: all,
    total: filtered.length,
    endpoints,
    isLoading,
    error,
    tierError,
    reload: useCallback(() => setReloadToken((token) => token + 1), []),
  };
}
